import React from 'react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import * as Sentry from '@sentry/nextjs';

import { wrapper } from '@store/store';
import { fetchGiftCardsDiscount, fetchGiftCardValues } from '@thunks/giftCards';
import { fetchCustomerDetail } from '@thunks/customerDetail';
import { selectIsUserLogged } from '@selectors/auth';
import { fetchBasketPaymentMethods } from '@store/thunks/basket';

import GiftCards from '@routes/GiftCards';

import { getRouteUrl } from '@utils/urls';
import { validateRedirectsByResolvedUrl } from '@utils/redirects';
import { getRequestInstance } from '@utils/request';

import { giftCardsPageDataShape } from '@common/types/giftCards';
import {
  GIFT_CARDS_VIEW_MODES,
  GIFT_CARDS_VIEW_MODE,
} from '@common/constants/giftCards';
import { giftCardsPage } from '@common/endpoints';
import { GIFT_CARDS_VIEW } from '@common/routes';

const GiftCardsPage = ({ pageData }) => <GiftCards pageData={pageData} />;

GiftCardsPage.propTypes = {
  pageData: giftCardsPageDataShape.isRequired,
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async context => {
    const { locale, resolvedUrl, query } = context;
    const { view } = query;
    const request = getRequestInstance(context);

    if (!GIFT_CARDS_VIEW_MODES[locale].includes(view)) {
      const validatedRedirects = validateRedirectsByResolvedUrl(resolvedUrl);

      if (validatedRedirects) {
        return {
          redirect: validatedRedirects,
        };
      }

      return {
        redirect: {
          statusCode: 302,
          destination: getRouteUrl(locale, GIFT_CARDS_VIEW, {
            view: GIFT_CARDS_VIEW_MODE[locale].PURCHASE,
          }),
        },
      };
    }

    const isUserLoggedIn = selectIsUserLogged(store.getState());

    const [{ data: pageData }] = await Promise.all([
      request.get(giftCardsPage()).catch(error => {
        if (error?.response?.status && error?.response?.status !== 404) {
          Sentry.captureException(error);
        }

        return {
          data: {
            meta: {},
            cardImage: null,
            body: null,
          },
        };
      }),
      store.dispatch(fetchBasketPaymentMethods()),
      store.dispatch(fetchGiftCardsDiscount()),
      store.dispatch(fetchGiftCardValues()),
      isUserLoggedIn ? store.dispatch(fetchCustomerDetail()) : null,
    ]);

    return {
      props: {
        ...(await serverSideTranslations(locale)),
        pageData,
      },
    };
  },
);

export default GiftCardsPage;
